import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { CreateGroupRequestDTO } from '@/core/api/dtos/request/group/create'
import { UpdateGroupRequestDTO } from '@/core/api/dtos/request/group/update'
import { UpdateGroupCampaignsRequestDTO } from '@/core/api/dtos/request/group/updateCampaigns'
import { UpdateGroupMixesRequestDTO } from '@/core/api/dtos/request/group/updateMixes'
import { UpdateGroupPlaylistsRequestDTO } from '@/core/api/dtos/request/group/updatePlaylists'
import { UpdateGroupSchedulesRequestDTO } from '@/core/api/dtos/request/group/updateSchedules'
import { UpdateGroupZonesRequestDTO } from '@/core/api/dtos/request/group/updateZones'

import apiClient from '@core/api/client'

export const groupService = {
	/**
	 * Paginate account groups
	 */
	paginateGroups(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/groups', config)
	},

	/**
	 * Get a group by id
	 */
	getGroup(
		groupId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/groups/${groupId}`, config)
	},

	/**
	 * Create a group
	 */
	createGroup(
		data: CreateGroupRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post('/my/groups', data, config)
	},

	/**
	 * Update a group
	 */
	patchGroup(
		groupId: number,
		data: UpdateGroupRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/groups/${groupId}`, data, config)
	},

	/**
	 * Delete a group
	 */
	deleteGroup(
		groupId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/groups/${groupId}`, config)
	},

	/**
	 * Fetch the zones of a group
	 */
	fetchGroupZones(
		groupId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/groups/${groupId}/zones`, config)
	},

	/**
	 * Update the zones of a group
	 */
	updateGroupZones(
		groupId: number,
		data: UpdateGroupZonesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/groups/${groupId}/zones`, data, config)
	},

	/**
	 * Add a zone to a group
	 */
	addZoneToGroup(
		groupId: number,
		zoneId: number,
		data: UpdateGroupZonesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/groups/${groupId}/zones/${zoneId}`, data, config)
	},

	/**
	 * Update the campaigns of a group
	 */
	updateGroupCampaigns(
		groupId: number,
		data: UpdateGroupCampaignsRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/groups/${groupId}/campaigns`, data, config)
	},

	/**
	 * Update the mixes of a group
	 */
	updateGroupMixes(
		groupId: number,
		data: UpdateGroupMixesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/groups/${groupId}/mixes`, data, config)
	},

	/**
	 * Get the schedules of a group
	 */
	getGroupSchedules(
		groupId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/groups/${groupId}/schedules`, config)
	},

	/**
	 * Update the schedules of a group
	 */
	updateGroupSchedules(
		groupId: number,
		data: UpdateGroupSchedulesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/groups/${groupId}/schedules`, data, config)
	},

	/**
	 * Add schedules to a group
	 */
	addSchedulesToGroup(
		groupId: number,
		data: UpdateGroupSchedulesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/groups/${groupId}/schedules`, data, config)
	},

	/**
	 * Remove schedules from a group
	 */
	removeSchedulesFromGroup(
		groupId: number,
		data: UpdateGroupSchedulesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/groups/${groupId}/schedules`, {
			...config,
			data,
		})
	},

	/**
	 * Get the playlists of a group
	 */
	getGroupPlaylists(
		groupId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/groups/${groupId}/playlists`, config)
	},

	/**
	 * Add playlists to a group
	 */
	addPlaylistsToGroup(
		groupId: number,
		data: UpdateGroupPlaylistsRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/groups/${groupId}/playlists`, data, config)
	},

	/**
	 * Remove playlists from a group
	 */
	removePlaylistsFromGroup(
		groupId: number,
		data: UpdateGroupPlaylistsRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/groups/${groupId}/playlists`, {
			...config,
			data,
		})
	},
}
